import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, Play } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { trackProductClick } from '../utils/analytics';

import { API_URL } from '../utils/apiUrl';

const CATEGORIES = [
  "Elektronik", "Giyim", "Kozmetik", "Ev Ürünleri",
  "Aksesuar", "Kişisel Bakım", "Gıda", "Diğer"
];

const ProductListingPage = () => {
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedVerdict, setSelectedVerdict] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchProducts(); }, [selectedCategory, selectedVerdict, searchTerm]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/products?published=true`;
      if (selectedCategory !== 'all') url += `&category=${encodeURIComponent(selectedCategory)}`;
      if (selectedVerdict !== 'all') url += `&verdict=${selectedVerdict}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      const { data } = await axios.get(url);
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-8 text-[#111]">Test Edilmiş Ürünler</h2>

        {/* Search */}
        <div className="relative max-w-md mb-6">
          <input type="text" placeholder="Ürün veya marka ara..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-neutral-300 px-5 py-3 text-sm rounded-lg focus:outline-none focus:border-[#1C3B2A] transition pr-10"
            data-testid="search-input" />
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        </div>

        {/* Verdict Filter */}
        <div className="flex flex-wrap gap-3 mb-8" data-testid="filter-buttons">
          <div className="flex gap-0 border border-neutral-300 rounded-md overflow-hidden">
            <button onClick={() => setSelectedVerdict('all')}
              className={`px-5 py-2.5 text-xs font-bold transition ${selectedVerdict === 'all' ? 'bg-neutral-900 text-white' : 'bg-white hover:bg-neutral-50'}`}
              data-testid="filter-all">TÜMÜ</button>
            <button onClick={() => setSelectedVerdict('test_basarili')}
              className={`px-5 py-2.5 text-xs font-bold transition border-l border-neutral-300 ${selectedVerdict === 'test_basarili' ? 'bg-[#1C3B2A] text-white' : 'bg-white hover:bg-neutral-50'}`}
              data-testid="filter-success">TEST BAŞARILI</button>
            <button onClick={() => setSelectedVerdict('beklentiyi_karsilamadi')}
              className={`px-5 py-2.5 text-xs font-bold transition border-l border-neutral-300 ${selectedVerdict === 'beklentiyi_karsilamadi' ? 'bg-[#A63333] text-white' : 'bg-white hover:bg-neutral-50'}`}
              data-testid="filter-fail">BEKLENTİYİ KARŞILAMADI</button>
          </div>
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2 mb-10">
          <button onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 text-xs font-bold rounded-full transition ${selectedCategory === 'all' ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`}>
            Tümü
          </button>
          {CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 text-xs font-bold rounded-full transition ${selectedCategory === cat ? 'bg-[#1C3B2A] text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`}>
              {cat}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-16 font-bold text-neutral-400" data-testid="loading-indicator">Yükleniyor...</div>
        ) : products.length === 0 ? (
          <div className="text-center py-16" data-testid="no-products">
            <p className="text-lg font-bold text-neutral-500 mb-4">Ürün bulunamadı</p>
            <button onClick={() => { setSelectedCategory('all'); setSelectedVerdict('all'); setSearchTerm(''); }}
              className="border-2 border-neutral-900 px-6 py-2.5 text-sm font-bold rounded-md hover:bg-neutral-900 hover:text-white transition">
              Filtreleri Temizle
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
            {products.map((product) => (
              <Link key={product.id} to={`/products/${product.id}`}
                onClick={() => trackProductClick(product)}
                className="bg-white border border-neutral-200 rounded-md hover:shadow-lg hover:-translate-y-1 transition-all duration-200 group overflow-hidden"
                data-testid={`product-card-${product.id}`}>
                <div className="relative aspect-video bg-neutral-900 overflow-hidden">
                  {product.image_url ? (
                    <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Play className="w-12 h-12 text-white/30" />
                    </div>
                  )}
                  <div className="absolute top-3 right-3 px-4 py-2 rounded-md shadow-lg"
                    style={{ backgroundColor: product.score_color }}>
                    <span className="text-white font-mono font-black text-xl">{product.score?.toFixed(1)}</span>
                    <span className="text-white/70 font-mono text-xs">/10</span>
                  </div>
                </div>
                <div className="p-5">
                  <span className="inline-block px-3 py-1.5 text-white text-[11px] font-bold rounded-md mb-2.5"
                    style={{ backgroundColor: product.score_color }}>
                    {product.verdict_display}
                  </span>
                  <h4 className="text-base font-black text-neutral-900 leading-tight mb-1">{product.name}</h4>
                  <p className="text-sm font-semibold text-neutral-500">{product.brand}</p>
                  <p className="text-xs font-medium text-neutral-400 mt-1">{product.category}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ProductListingPage;
